export type BookingFailureType = {
   bookingId: string;
   timestamp: string;
   user: {
      firstName: string;
      lastName: string;
      email: string;
      fileImage: {
         s3Path: string;
      };
   };
   errorType: string;
   status: string;
   property: string;
   amount: number;
};

export const BookingFailureLogErrors = [
   'Payment Gateway Error',
   'Server Error',
   'Rate Limit',
   'Payment Timeout',
   'Unknown',
   'Network Error',
   'Validation Error',
   'Session Expired',
   'Payment Declined',
] as const;

export const BookingFailureLogStatus = ['RESOLVED', 'NEW', 'INVESTIGATING', 'IGNORED'] as const;

export type BookingFailureLogErrorType = (typeof BookingFailureLogErrors)[number];

export type BookingFailureLogStatusType = (typeof BookingFailureLogStatus)[number];

export type BookingFailureLog = {
   id: string;
   siteName: string;
   bookingId: string;
   timestamp: string;
   user: User;
   errorType: BookingFailureLogErrorType;
   status: BookingFailureLogStatusType;
   property: string;
   amount: string;
   errorMessage: string;
};

export type BookingFailureLogsStatistics = {
   failureRate24h: number;
   failureDiffRate24h: number;
   failure24h: number;
   failureDiff24h: number;
   breakdown7Days: Breakdown7DaysItem[];
   topErrorType: TopErrorType;
};

type BookingError = {
   type: string;
   code: number;
   message: string;
};

type LocationContext = {
   place: { id: string; name: string };
   street: { id: string; name: string };
   address: {
      id: string;
      name: string;
      street_name: string;
      address_number: string;
   };
   country: { id: string; name: string; country_code: string; country_code_alpha_3: string };
   locality: { id: string; name: string };
   postcode: { id: string; name: string };
   neighborhood: { id: string; name: string };
};

type MetadataInformation = {
   os: string;
   device: string;
   browser: string;
   network: string;
   location: string;
};

export type BookingErrorLogNote = {
   id: string;
   note: string;
   createdBy: string;
   createdByUser: User;
   created: string;
};

export type BookingErrorLogStatusHistory = {
   id: string;
   oldStatus: BookingFailureLogStatusType | null;
   newStatus: BookingFailureLogStatusType;
   createdBy: string;
   createdByUser: User;
   created: string;
   notes: BookingErrorLogNote[];
};

export type BookingFailureLogResponse = {
   id: string;
   siteName: string;
   booking: Booking;
   user: User;
   organization: Organization;
   status: BookingFailureLogStatusType;
   errorDetail: ErrorDetail;
   metadata: Metadata;
   created: string;
   step: string;
   relatedLogs: BookingFailureLogResponse[];
   statusHistories: BookingErrorLogStatusHistory[];
};

type Failures24h = {
   count: number;
   diffPercentage: number;
};

type FailureRate24h = {
   rate: number;
   diffPercentage: number;
};

export type Breakdown7DaysItem = {
   type: string;
   count: number;
   percentage: number;
};

type TopErrorType = {
   type: string;
   occurrences: number;
   rateOnTotalErrors: number;
};

type Booking = {
   id: string;
   checkinDate: string;
   checkoutDate: string;
   paidAmount: string;
   adultNumber: number;
   metadata: {
      netRate: number;
      proceeds: number;
      saleRate: number;
      hotelName: string;
      commission: number;
      hotelRegion: string;
      nightNumber: number;
      roomNightly: number;
      hotelAddress: string;
      hotelZipCode: string;
      bonusProceeds: number;
      discountAmount: number;
      CCTransactionFee: number;
      excludedTaxAndFee: {
         city_tax: { amount: number; currency: string };
         cleaning_fee: { amount: number; currency: string };
         occupancy_tax: { amount: number; currency: string };
      };
      includedTaxAmount: number;
      incrementalTaxEst: number;
      priceBeforeDiscount: number;
      proceedsBeforeDiscount: number;
      customerSuccessUtilized: boolean;
   };
};

type User = {
   firstName: string;
   lastName: string;
   avatar: string;
   emailAddress: string;
   ip: string;
   id: string;
};

type Organization = {
   name: string;
   slug: string;
   image: string;
};

type ErrorDetail = {
   error: string;
   message: string;
   statusCode: number;
   type: string;
};

type Metadata = {
   infomation: MetadataInformation;
};

export type BookingFailureLogsStatisticsResponse = {
   failures24h: Failures24h;
   failureRate24h: FailureRate24h;
   breakdown7Days: Breakdown7DaysItem[];
   topErrorType: TopErrorType;
};
